<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<!-- eslint-disable vue/no-root-v-if -->
<template>
  <div
    v-if="files.length > 0"
    class="fixed bottom-0 right-0 w-full sm:w-30rem surface-0 border-round shadow-5 m-0 sm:m-3 z-5 p-3 surface-border surface-ground border-1"
  >
    <div class="flex justify-content-between align-items-center">
      <strong>{{ overallMessage }}</strong>
      <div>
        <Button
          :icon="isReduced ? 'ri-arrow-up-line' : 'ri-arrow-down-line'"
          @click="changeReducedState"
          text
          rounded
          severity="secondary"
          aria-label="Réduire"
        />
        <Button
          v-if="!isUploading"
          icon="ri-close-line"
          @click="clearCompleted"
          text
          rounded
          severity="secondary"
          aria-label="Fermer"
        />
      </div>
    </div>
    <div class="file-list" v-if="!isReduced">
      <div v-for="file in files" :key="file.name" class="file-item">
        <div class="flex flex-column pr-1">
          <a
            v-if="file.status === 'success'"
            class="file-name cursor-pointer"
            @click="openFile(file)"
            >{{ file.name }}</a
          >
          <span v-else class="file-name">{{ file.name }}</span>
          <p v-if="file.status === 'error'" class="error-message text-xs">
            {{ file.message }}
          </p>
        </div>
        <div class="status-container">
          <PrimeKnob
            v-if="file.status === 'uploading'"
            v-model="file.percent"
            :size="50"
            :strokeWidth="5"
            readonly
            valueTemplate="{value}%"
            class="upload-knob"
          />
          <div v-if="file.status === 'rejected'">
            {{ $t("canceledImport") }}
          </div>
          <i
            v-else
            :class="`${iconForStatus(file.status)}`"
            :style="getIconColor(file.status)"
          ></i>
          <Button
            v-if="file.status === 'uploading' && file.percent !== 100"
            icon="ri-close-line"
            text
            rounded
            severity="danger"
            @click="cancelFileUpload(file.name)"
            aria-label="Annuler"
            class="cancel-btn"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import Knob from "primevue/knob";
import { useUploadStore } from "@/stores/upload.js";

export default {
  name: "UploadStatus",
  components: {
    PrimeKnob: Knob,
  },
  emits: ["openFile"],
  data() {
    return {
      isReduced: false,
    };
  },
  computed: {
    ...mapState(useUploadStore, [
      "files",
      "isUploading",
      "overallMessage",
      "activeRequests",
    ]),
  },
  methods: {
    ...mapActions(useUploadStore, ["clearCompleted", "cancelUpload"]),
    changeReducedState() {
      this.isReduced = !this.isReduced;
    },
    cancelFileUpload(fileName) {
      this.cancelUpload(fileName);
    },
    getIconColor(status) {
      switch (status) {
        case "success":
          return "color: green";
        case "error":
          return "color: red";
        default:
          return "";
      }
    },
    iconForStatus(status) {
      switch (status) {
        case "success":
          return "ri-checkbox-circle-fill text-2xl";
        case "error":
          return "ri-close-circle-fill text-2xl";
        default:
          return "";
      }
    },
    openFile(node) {
      this.$emit("openFile", node);
    },
  },
};
</script>

<style scoped>
.file-list {
  max-height: 20rem;
  overflow-y: auto;
  font-size: 0.9rem;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1rem;
  border-top: 0.0625rem solid #eee;
}

.file-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 0.3125rem;
}

.status-container {
  display: flex;
  align-items: center;
  gap: 0.3125rem;
}

.error-message {
  color: red;
}
</style>
